"use client";

import { useState, useEffect } from "react";
import { useCommsPanel } from "../CommsProvider";
import { ScriptSection } from "./ScriptSection";

interface ScriptSectionData {
  id: string;
  title: string;
  scriptText: string | null;
  sectionType: string;
  linkedFields: string[];
  displayOrder: number;
  active: boolean;
}

interface FieldConfigData {
  fieldKey: string;
  label: string;
  fieldType: string;
  options: unknown | null;
  placeholder: string | null;
  mandatory: boolean;
  visible: boolean;
}

interface CallScriptViewProps {
  onEndCall: (sectionNotes: Record<string, string>) => void;
}

export function CallScriptView({ onEndCall }: CallScriptViewProps) {
  const { activeLeadId, activeLeadName } = useCommsPanel();
  const [sections, setSections] = useState<ScriptSectionData[]>([]);
  const [fieldConfigs, setFieldConfigs] = useState<FieldConfigData[]>([]);
  const [qualificationData, setQualificationData] = useState<Record<string, unknown>>({});
  const [sectionNotes, setSectionNotes] = useState<Record<string, string>>({});
  const [agentName, setAgentName] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [scriptRes, configRes, qualRes, sessionRes] = await Promise.all([
          fetch("/api/admin/config/call-script"),
          fetch("/api/admin/config/qualification"),
          activeLeadId ? fetch(`/api/leads/${activeLeadId}/qualification`) : Promise.resolve(null),
          fetch("/api/auth/session"),
        ]);

        if (scriptRes.ok) {
          const data = await scriptRes.json();
          setSections((Array.isArray(data) ? data : data.data ?? []).filter((s: ScriptSectionData) => s.active).sort((a: ScriptSectionData, b: ScriptSectionData) => a.displayOrder - b.displayOrder));
        }
        if (configRes.ok) {
          const data = await configRes.json();
          setFieldConfigs((Array.isArray(data) ? data : data.data ?? []).filter((f: FieldConfigData) => f.visible));
        }
        if (qualRes && qualRes.ok) {
          const data = await qualRes.json();
          setQualificationData(data ?? {});
        }
        if (sessionRes.ok) {
          const data = await sessionRes.json();
          setAgentName(data?.user?.name ?? data?.user?.email ?? "");
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [activeLeadId]);

  /** Replace {{placeholders}} in script text with lead/agent context */
  function interpolateScript(text: string | null): string | null {
    if (!text) return text;
    const leadFirst = activeLeadName?.split(" ")[0] ?? "";
    return text
      .replace(/\{\{leadName\}\}/g, activeLeadName ?? "")
      .replace(/\{\{firstName\}\}/g, leadFirst)
      .replace(/\{\{agentName\}\}/g, agentName);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-xs text-gray-400">
        Loading call script...
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="py-6 text-center text-xs text-gray-400">
        No call script sections configured. Ask your admin to set up the call script.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-green-100 px-3 py-2">
        <p className="text-xs font-medium text-green-800">
          Call Script — {activeLeadName ?? "Lead"}
        </p>
      </div>

      {sections.map((section) => {
        const linked = section.linkedFields
          .map((fk) => fieldConfigs.find((fc) => fc.fieldKey === fk))
          .filter(Boolean) as FieldConfigData[];

        return (
          <ScriptSection
            key={section.id}
            title={section.title}
            scriptText={interpolateScript(section.scriptText)}
            sectionType={section.sectionType}
            linkedFields={linked}
            sectionNotes={sectionNotes[section.id] ?? ""}
            onNotesChange={(notes) => setSectionNotes((prev) => ({ ...prev, [section.id]: notes }))}
            qualificationData={qualificationData}
          />
        );
      })}

      <button
        onClick={() => onEndCall(sectionNotes)}
        className="w-full rounded-lg bg-red-500 py-2 text-xs font-medium text-white hover:bg-red-600"
      >
        End Call — Record Outcome
      </button>
    </div>
  );
}
